/**
 * CloudWatch RUM client bootstrap (SP9.02 #14).
 *
 * No-op when config.json omits the `rum` block — safe to deploy before the
 * Terraform RUM resources exist. User identifier is hashed (SHA-256 prefix)
 * to avoid leaking PII into telemetry given the regulated-product context
 * (see SP9.02 Q6).
 */

import { AwsRum, type AwsRumConfig } from 'aws-rum-web';
import type { RumConfig } from '@/types';

let client: AwsRum | null = null;

export function initRum(rum: RumConfig): AwsRum | null {
  if (client) return client;
  const config: AwsRumConfig = {
    identityPoolId: rum.identityPoolId,
    endpoint: `https://dataplane.rum.${rum.region}.amazonaws.com`,
    telemetries: ['errors', 'performance', 'http'],
    allowCookies: true,
    enableXRay: false,
    sessionSampleRate: 1,
  };
  try {
    client = new AwsRum(rum.appMonitorId, '1.0.0', rum.region, config);
    return client;
  } catch (err) {
    // RUM failures must not break the SPA. Log locally only.
    console.warn('[rum] init failed', err);
    return null;
  }
}

export async function identifyRumUser(userId: string): Promise<void> {
  if (!client) return;
  const hashed = await sha256Prefix(userId);
  try {
    client.addSessionAttributes({ userIdHash: hashed });
  } catch (err) {
    console.warn('[rum] identify failed', err);
  }
}

export async function sha256Prefix(input: string): Promise<string> {
  if (typeof crypto === 'undefined' || !crypto.subtle) return 'anon';
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  const bytes = Array.from(new Uint8Array(buf)).slice(0, 8);
  return bytes.map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function getRum(): AwsRum | null {
  return client;
}
